import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  dismissExtractionJob,
  listExtractionJobs,
  startContractExtraction,
} from './uploadContractApi';

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc'];
const MAX_BYTES = 50 * 1024 * 1024; // matches the backend multer fileSize limit in server.js

function fileKey(file) {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

function validateFile(file) {
  const lowered = file.name.toLowerCase();
  const ext = lowered.substring(lowered.lastIndexOf('.'));
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `${file.name}: invalid type (PDF, DOCX, or DOC only).`;
  }
  if (file.size > MAX_BYTES) {
    return `${file.name}: too large (max 50MB).`;
  }
  return null;
}

export default function useUploadContractExtractionJobs({
  setError,
  showToast,
}) {
  const [jobs, setJobs] = useState([]);
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState({ done: 0, total: 0 });
  const jobsRef = useRef([]);

  jobsRef.current = jobs;

  const stageFiles = useCallback((incoming) => {
    setError(null);
    const accepted = [];
    const errors = [];
    for (const file of incoming) {
      const reason = validateFile(file);
      if (reason) {
        errors.push(reason);
      } else {
        accepted.push(file);
      }
    }
    if (errors.length) {
      setError(errors.join(' '));
    }
    if (accepted.length) {
      setFiles((prev) => {
        const seen = new Set(prev.map(fileKey));
        const merged = [...prev];
        for (const file of accepted) {
          const key = fileKey(file);
          if (!seen.has(key)) {
            merged.push(file);
            seen.add(key);
          }
        }
        return merged;
      });
    }
  }, [setError]);

  // Kept for compatibility with the single-file callsite. Folds a
  // single file into the staged list using the same validation path.
  const handleFileSelect = useCallback((file) => {
    stageFiles([file]);
  }, [stageFiles]);

  const handleFilesSelect = useCallback((selected) => {
    if (!selected || selected.length === 0) return;
    stageFiles(Array.from(selected));
  }, [stageFiles]);

  const removeStagedFile = useCallback((key) => {
    setFiles((prev) => prev.filter((file) => fileKey(file) !== key));
  }, []);

  const clearStagedFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length) {
      stageFiles(Array.from(dropped));
    }
  }, [stageFiles]);

  const refreshJobs = useCallback(async () => {
    try {
      const data = await listExtractionJobs();
      const nextJobs = data.jobs || [];
      setJobs(nextJobs);
      return nextJobs;
    } catch (err) {
      // Transient — silently swallow, next tick will retry.
      return jobsRef.current;
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!files.length) return;

    setUploading(true);
    setUploadCount({ done: 0, total: files.length });
    setError(null);

    const failures = [];
    let succeeded = 0;

    // Sequential to avoid a thundering herd of multer uploads + Claude
    // calls. Each iteration registers a job that runs in the background
    // on the server, so total wall-clock is just the upload time.
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        await startContractExtraction(formDataUpload);
        succeeded += 1;
      } catch (err) {
        failures.push(`${file.name}: ${err.message || 'upload failed'}`);
      }
      setUploadCount({ done: i + 1, total: files.length });
    }

    if (failures.length) {
      const message = failures.join(' ');
      setError(message);
      showToast(message, 'error');
    }
    if (succeeded > 0) {
      showToast(
        succeeded === 1
          ? 'Extraction started — this usually takes 1–5 minutes'
          : `${succeeded} extractions started — this usually takes 1–5 minutes each`,
        'info'
      );
    }

    setFiles([]);
    setUploadCount({ done: 0, total: 0 });
    setUploading(false);
    refreshJobs();
  }, [files, refreshJobs, setError, showToast]);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    async function tick() {
      if (cancelled) return;
      const nextJobs = await refreshJobs();
      if (cancelled) return;
      const hasRunning = nextJobs.some((job) => job.status === 'running');
      timer = setTimeout(tick, hasRunning ? 3000 : 15000);
    }
    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [refreshJobs]);

  const dismissJob = useCallback(async (jobId) => {
    try {
      await dismissExtractionJob(jobId);
      setJobs((prev) => prev.filter((job) => job.jobId !== jobId));
    } catch (err) {
      showToast('Could not dismiss job', 'error');
    }
  }, [showToast]);

  const uploadProgress = useMemo(() => {
    if (!uploadCount.total) return 0;
    return Math.round((uploadCount.done / uploadCount.total) * 100);
  }, [uploadCount]);

  return {
    clearStagedFiles,
    dismissJob,
    dragActive,
    files,
    handleDrag,
    handleDrop,
    handleFileSelect,
    handleFilesSelect,
    handleUpload,
    jobs,
    removeStagedFile,
    uploadCount,
    uploadProgress,
    uploading,
  };
}
